import React, { FC, useCallback } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { LoginFormData, OnboardingAside, OnboardingContent } from "@xapp/components";

import { setToken, setProfile } from "auth";
import { Credentials } from "auth/modules/state";
import { UserProfile } from "generated/graphql";
import { State } from "redux/state";

import { FetchProfileComponent } from "components/Authorize/components/FetchProfileComponent";
import { SignUp, SignUpProps, SignUpSession } from "components/Login/components/SignUp";
import { OnboardingProps } from "components/Onboarding/models";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback(
        (newParams: QueryParamsType) => {
            const searchParams = new URLSearchParams(history.location.search);
            for (const key in newParams) {
                searchParams.set(key, newParams[key]);
            }
            history.replace({ ...history.location, search: searchParams.toString() });
        },
        [history]
    );
}

export interface SignUpStepContainerProps
    extends Omit<SignUpProps, "displaySocialLogins" | "onSignUpSuccess" | "onCodeVerified" | "onSignIn"> {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Props for the onboarding
     */
    onboarding: OnboardingProps;
    /**
     * On sign up success
     */
    onSignUpSuccess?(props: OnboardingProps): void;
}

/**
 * V3 - Used for in flow sign-up
 *
 * TODO: Rename this to a Container
 */
export const SignUpStepContainer: FC<SignUpStepContainerProps> = (props) => {
    const { onboarding, onSignUpSuccess } = props;

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const dispatch = useDispatch();
    const credentials = useSelector<State, Credentials>((state) => state.auth.credentials);
    const profile = useSelector<State, UserProfile>((state) => state.auth.profile); // useProfile();

    const displayCodeDialog = queryParams.get("displayCode") === "true";
    const email = queryParams.get("email");

    // redirect to the next step if we have a profile
    const handleSignUpSuccess = useCallback(
        (data: LoginFormData) => {
            // they signed up, set #verify
            // keep existing

            const queryParams: { [key: string]: string } = {
                displayCode: "true",
                email: data.email,
            };

            if (data.website) {
                queryParams.website = data.website;
            }

            setQueryParams(queryParams);
        },
        [setQueryParams]
    );

    // if they verified the code, we can move on
    const handleSignUpWithCodeVerified = useCallback(
        (data: LoginFormData) => {
            const queryParams: { [key: string]: string } = {
                email: data.email,
            };
            if (data.website) {
                queryParams.website = data.website;
            }

            setQueryParams(queryParams);
        },
        [setQueryParams]
    );

    // if they signed in, we can move on
    const handleSignIn = useCallback(
        (session: SignUpSession, data: LoginFormData) => {
            dispatch(setToken(session));
        },
        [dispatch]
    );

    const handleProfile = useCallback(
        (profile: UserProfile) => {
            dispatch(setProfile(profile));
        },
        [dispatch]
    );

    // if we have credentials, we logged in successfully
    // see the logic over in AuthorizeComponent
    // this is very similar to the if (token) { <FetchProfile}
    if (credentials && !profile) {
        return (
            <OnboardingContent>
                <FetchProfileComponent
                    accessToken={credentials.accessToken}
                    onError={(e) => {
                        console.log(`error fetching profile ${e}`);
                    }}
                    onProfile={handleProfile}
                />
            </OnboardingContent>
        );
    }

    // After they sign up / OR / if they already are signed in, we then call the callback
    if (profile) {
        // this was using during sign_up
        localStorage.removeItem("xa_redirect");
        const copy = { ...onboarding };
        // Add the profile data
        copy.leadsContact = profile.email;
        onSignUpSuccess(copy);
    }

    function getInnerComponents(recaptchaKey?: string) {
        return (
            <>
                {props.aside && <OnboardingAside>{props.aside}</OnboardingAside>}
                <OnboardingContent>
                    <SignUp
                        {...props}
                        title={"Sign Up to Claim"}
                        subtitle={"Claim your business to manage your listing"}
                        data={{ email }}
                        displaySocialLogins={true}
                        showVerificationCodeDialog={displayCodeDialog}
                        onSignUpSuccess={handleSignUpSuccess}
                        onCodeVerified={handleSignUpWithCodeVerified}
                        onSignIn={handleSignIn}
                        recaptchaSiteKey={recaptchaKey}
                    />
                </OnboardingContent>
            </>
        );
    }

    const reCaptchaKey = window?.xaStudioConfig?.reCaptchaSiteKey;

    if (reCaptchaKey) {
        // Only applying GoogleReCaptchaProvider to the Onboarding Signup container
        // as this is where we have seen the most bots in the past
        // We can reconsider this in the future.
        return (
            <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
                {getInnerComponents(reCaptchaKey)}
            </GoogleReCaptchaProvider>
        );
    } else {
        return getInnerComponents();
    }
};
